import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { FormData, SalesforceResponse, TokenResponse, ContentVersionResponse } from '.';

type SubmitFormPayload = {
	formData: FormData;
	files: File[];
};

export const useSubmitAcrForm = (): UseMutationResult<void, Error, SubmitFormPayload> => {
	const instanceUrl = process.env.REACT_ACR_URL!;
	const apiVersion = process.env.REACT_ACR_FORM_VERSION!;
	const acrGrantType = process.env.REACT_ACR_GRANT_TYPE!;
	const acrClientId = process.env.REACT_ACR_CLIENT_ID!;
	const acrClientSecret = process.env.REACT_ACR_CLIENT_SECRET!;

	const fileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (): void => {
				if (reader.result && typeof reader.result === 'string') {
					// Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
					const base64Data = reader.result.split(',')[1];
					if (base64Data) {
						resolve(base64Data);
					} else {
						reject(new Error('Failed to extract base64 data from file'));
					}
				} else {
					reject(new Error('FileReader result is not a string'));
				}
			};
			reader.onerror = (): void => {
				reject(new Error(`Error reading file: ${file.name}`));
			};
			reader.readAsDataURL(file);
		});
	};

	const getAccessToken = async (): Promise<{ accessToken: string; baseUrl: string }> => {
		const tokenRes = await fetch(`${instanceUrl}/services/oauth2/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
				Accept: 'application/json',
			},
			body: new URLSearchParams({
				grant_type: acrGrantType,
				client_id: acrClientId,
				client_secret: acrClientSecret,
			}),
		});

		if (!tokenRes.ok) {
			const errorText = await tokenRes.text();
			throw new Error(`Token request failed (${tokenRes.status}): ${errorText}`);
		}

		const tokenData: TokenResponse = await tokenRes.json();
		if (!tokenData.access_token) {
			throw new Error(`Invalid token response: ${JSON.stringify(tokenData)}`);
		}

		return {
			accessToken: tokenData.access_token,
			baseUrl: tokenData.instance_url,
		};
	};

	const createCase = async (formData: FormData, accessToken: string, baseUrl: string): Promise<string> => {
		const caseRes = await fetch(`${baseUrl}/services/data/${apiVersion}/sobjects/Case`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({
				Subject: 'Case from Web Form',
				SuppliedName: formData.name,
				SuppliedPhone: formData.mobile,
				Employee_Code__c: formData.employeeCode,
				Please_enter_the_date_of_the_Complaint__c: formData.complaintDate,
				Please_Select_Department_Division__c: formData.department,
				Please_select_the_type_of_product__c: formData.productType,
				State__c: formData.state,
				City__c: formData.city,
				Name_of_the_Pidilite_product_infringed__c: formData.productName,
				Customer_Type__c: formData.customerType,
				Address__c: formData.address,
				Others__c: formData.otherSpecify,
				Sample_be_sent_to_Head_Office__c: formData.sampleDate,
				Chapter_Name1__c: 'WSS Service Cell',
				Origin: 'ACR Form',
			}),
		});

		if (!caseRes.ok) {
			const errorText = await caseRes.text();
			throw new Error(`Case creation failed (${caseRes.status}): ${errorText}`);
		}

		const caseResult: SalesforceResponse = await caseRes.json();
		if (!caseResult.id) {
			throw new Error(`Case creation failed: No ID returned - ${JSON.stringify(caseResult)}`);
		}
		return caseResult.id;
	};

	const uploadFile = async (file: File, accessToken: string, baseUrl: string): Promise<string> => {
		const maxSize = 35 * 1024 * 1024; // 35MB
		if (file.size > maxSize) {
			throw new Error(
				`File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 35MB.`
			);
		}

		// Convert file to base64
		const base64Data = await fileToBase64(file);

		// Upload file as ContentVersion
		const uploadRes = await fetch(`${baseUrl}/services/data/${apiVersion}/sobjects/ContentVersion`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({
				Title: file.name,
				PathOnClient: file.name,
				VersionData: base64Data,
				FirstPublishLocationId: null,
			}),
		});

		if (!uploadRes.ok) {
			const errorText = await uploadRes.text();
			throw new Error(`File upload failed for "${file.name}" (${uploadRes.status}): ${errorText}`);
		}

		const uploadResult: SalesforceResponse = await uploadRes.json();
		if (!uploadResult.id) {
			throw new Error(`File upload failed for "${file.name}": No ContentVersion ID returned`);
		}

		return uploadResult.id;
	};

	const getContentDocumentId = async (
		contentVersionId: string,
		accessToken: string,
		baseUrl: string
	): Promise<string> => {
		const query = `SELECT ContentDocumentId FROM ContentVersion WHERE Id='${contentVersionId}'`;
		const encodedQuery = encodeURIComponent(query);

		const versionInfoRes = await fetch(`${baseUrl}/services/data/${apiVersion}/query/?q=${encodedQuery}`, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				Accept: 'application/json',
			},
		});

		if (!versionInfoRes.ok) {
			const errorText = await versionInfoRes.text();
			throw new Error(`Failed to get ContentDocument ID (${versionInfoRes.status}): ${errorText}`);
		}

		const versionJson: ContentVersionResponse = await versionInfoRes.json();
		if (!versionJson.records || versionJson.records.length === 0) {
			throw new Error(`No ContentVersion record found for ID: ${contentVersionId}`);
		}

		return versionJson.records[0].ContentDocumentId;
	};

	const linkFileToCase = async (
		contentDocumentId: string,
		caseId: string,
		accessToken: string,
		baseUrl: string
	): Promise<SalesforceResponse> => {
		const linkRes = await fetch(`${baseUrl}/services/data/${apiVersion}/sobjects/ContentDocumentLink`, {
			method: 'POST',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({
				ContentDocumentId: contentDocumentId,
				LinkedEntityId: caseId,
				ShareType: 'V', // Viewer access
				Visibility: 'AllUsers',
			}),
		});

		if (!linkRes.ok) {
			const errorText = await linkRes.text();
			throw new Error(`Failed to link file to case (${linkRes.status}): ${errorText}`);
		}

		const linkResult = await linkRes.json();
		return linkResult;
	};

	return useMutation({
		mutationFn: async (params: SubmitFormPayload) => {
			const { formData, files } = params;

			try {
				const { accessToken, baseUrl } = await getAccessToken();
				const caseId = await createCase(formData, accessToken, baseUrl);

				if (files && files.length > 0) {
					for (const file of files) {
						try {
							const contentVersionId = await uploadFile(file, accessToken, baseUrl);
							const contentDocumentId = await getContentDocumentId(contentVersionId, accessToken, baseUrl);
							await linkFileToCase(contentDocumentId, caseId, accessToken, baseUrl);
						} catch (fileError) {
							throw new Error(
								`Failed to process file "${file.name}": ${
									fileError instanceof Error ? fileError.message : 'Unknown error'
								}`
							);
						}
					}
				}
			} catch (error) {
				throw error instanceof Error ? error : new Error(`Form submission failed: ${JSON.stringify(error)}`);
			}
		},
	});
};
